import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, ExternalLink, FileText, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/common/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useHousehold } from "@/context/HouseholdContext";
import { useAssets } from "@/hooks/use-assets";
import { useHouseholdDetail } from "@/hooks/use-household-detail";
import { api, formatApiError } from "@/lib/api";

const EXT = { "application/pdf": "pdf", "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };

// Fetches through the authenticated client (works in the installed PWA and on mobile), then opens or saves the file.
const useDocFile = (hid) => {
  const [busy, setBusy] = useState(null);
  const run = async (d, mode) => {
    const win = mode === "open" ? window.open("", "_blank") : null;
    setBusy(`${d.id}-${mode}`);
    try {
      const r = await api.get(`/households/${hid}/files/${d.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (win) { win.location.href = url; } else {
        Object.assign(document.createElement("a"), { href: url, download: `${d.title}.${EXT[d.content_type] || "pdf"}` }).click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { win?.close(); toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  return { busy, run };
};

export const DOC_KINDS = { facture: "Facture", contrat: "Contrat", notice: "Notice", garantie: "Garantie", autre: "Autre" };

export const useDocuments = () => {
  const { active } = useHousehold();
  return useQuery({ queryKey: ["documents", active?.id], queryFn: () => api.get(`/households/${active.id}/documents`).then((r) => r.data), enabled: !!active });
};

const UploadForm = ({ assetId, onDone }) => {
  const { active } = useHousehold();
  const { data: assets = [] } = useAssets();
  const ref = useRef();
  const [v, setV] = useState({ title: "", kind: "facture", doc_date: "", asset_id: assetId || "none" });
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    const file = ref.current.files[0];
    if (!file) return toast.error("Choisissez un fichier");
    const fd = new FormData();
    fd.append("file", file); fd.append("title", v.title); fd.append("kind", v.kind);
    if (v.doc_date) fd.append("doc_date", v.doc_date);
    if (v.asset_id !== "none") fd.append("asset_id", v.asset_id);
    setBusy(true);
    try {
      await api.post(`/households/${active.id}/documents`, fd);
      ref.current.value = "";
      setFileName("");
      setV({ ...v, title: "" });
      toast.success("Document ajouté");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className={`card-surface grid gap-2 p-4 ${assetId ? "sm:grid-cols-2" : "md:grid-cols-[1fr_9rem_12rem_12rem]"}`} data-testid="document-form">
      <Input value={v.title} onChange={set("title")} placeholder="Titre (ex. Facture TV)" required minLength={2} data-testid="document-title-input" />
      <Select value={v.kind} onValueChange={set("kind")}><SelectTrigger data-testid="document-kind-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(DOC_KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      <DatePicker value={v.doc_date} onChange={set("doc_date")} placeholder="Date du document" data-testid="document-date-input" />
      {!assetId && <Select value={v.asset_id} onValueChange={set("asset_id")}><SelectTrigger data-testid="document-asset-select"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="none">Aucun équipement</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>}
      <label className={`flex h-10 min-w-0 cursor-pointer items-center gap-2 rounded-md border border-dashed border-input bg-background px-3 text-sm ${assetId ? "" : "md:col-span-3"}`} data-testid="document-file-label">
        <Upload className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="shrink-0 font-medium">Choisir un fichier</span>
        <span className="min-w-0 truncate text-muted-foreground" data-testid="document-file-name">{fileName || "Aucun fichier sélectionné"}</span>
        <input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="sr-only" onChange={(e) => setFileName(e.target.files?.[0]?.name || "")} data-testid="document-file-input" />
      </label>
      <Button type="submit" className="rounded-full" disabled={busy} data-testid="document-submit-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Ajouter</Button>
    </form>
  );
};

export const DocumentList = ({ docs, canEdit, onDone }) => {
  const { active } = useHousehold();
  const { data: assets = [] } = useAssets();
  const { busy, run } = useDocFile(active?.id);
  const remove = async (d) => { try { await api.delete(`/households/${active.id}/documents/${d.id}`); toast.success("Document supprimé"); onDone(); } catch (e) { toast.error(formatApiError(e)); } };
  if (!docs.length) return <p className="text-sm text-muted-foreground">Aucun document.</p>;
  return (
    <ul className="divide-y divide-border" data-testid="documents-list">
      {docs.map((d) => (
        <li key={d.id} className="flex items-center gap-3 py-3 text-sm" data-testid={`document-${d.id}`}>
          <span className="tone-chip tone-sky flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"><FileText className="h-4 w-4" /></span>
          <button type="button" disabled={!d.file_id} onClick={() => run(d, "open")} className="min-w-0 flex-1 text-left disabled:cursor-default" data-testid={`document-open-${d.id}`}>
            <span className="block truncate font-medium hover:underline">{d.title}</span>
            <span className="text-xs text-muted-foreground">{DOC_KINDS[d.kind]}{d.doc_date && ` · ${new Date(d.doc_date).toLocaleDateString("fr-FR")}`}{d.asset_id && ` · ${assets.find((a) => a.id === d.asset_id)?.name || ""}`}{d.is_demo && " · document fictif"}</span>
          </button>
          {d.file_id && <>
            <button type="button" onClick={() => run(d, "open")} aria-label="Ouvrir" title="Ouvrir" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-secondary sm:h-8 sm:w-8" data-testid={`document-view-${d.id}`}>
              {busy === `${d.id}-open` ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}</button>
            <button type="button" onClick={() => run(d, "download")} aria-label="Télécharger" title="Télécharger" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-secondary sm:h-8 sm:w-8" data-testid={`document-download-${d.id}`}>
              {busy === `${d.id}-download` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</button>
          </>}
          {canEdit && <ConfirmDelete title="Supprimer ce document ?" text={`« ${d.title} » sera supprimé définitivement.`} testid={`document-delete-${d.id}`} onConfirm={() => remove(d)} />}
        </li>
      ))}
    </ul>
  );
};

export const DocumentsPanel = ({ assetId }) => {
  const qc = useQueryClient();
  const { canEdit } = useHouseholdDetail();
  const { data = [] } = useDocuments();
  const [kind, setKind] = useState("all");
  const docs = data.filter((d) => (!assetId || d.asset_id === assetId) && (kind === "all" || d.kind === kind));
  const refresh = () => ["documents", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  return (
    <section className="space-y-4" data-testid={assetId ? "asset-documents" : "documents-panel"}>
      {canEdit && <UploadForm assetId={assetId} onDone={refresh} />}
      {!assetId && (
        <div className="flex flex-wrap gap-1.5">
          {["all", ...Object.keys(DOC_KINDS)].map((k) => (
            <button key={k} onClick={() => setKind(k)} className={`rounded-full px-3 py-1 text-xs transition-colors ${kind === k ? "bg-primary text-primary-foreground" : "bg-secondary"}`} data-testid={`documents-filter-${k}`}>{k === "all" ? "Tous" : DOC_KINDS[k]}</button>
          ))}
        </div>
      )}
      <div className="card-surface px-5 py-2"><DocumentList docs={docs} canEdit={canEdit} onDone={refresh} /></div>
      {!assetId && <p className="text-xs text-muted-foreground">PDF ou images, 10 Mo max. Stockage privé, visible uniquement par les membres du foyer.</p>}
    </section>
  );
};
